import { useAuth } from "@/lib/contexts/auth.context";
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useCreatePodWorkspaceFlow } from "@/features/podWorkspace/context/useCreatePodWorkspaceFlow.hook";
import { getPlayerActions, usePlayerStore } from "@/lib/player/player.store";
import { PodcastService } from "@/lib/podcast/podcast.service";
import { toast } from "sonner";
import { GlobalChat } from "@/features/chat/components/globalChat.component";
import {
	usePodWorkspaceStore,
	getWorkspaceActions,
} from "@/lib/podWorkspace/podWorkspace.store";
import { RecentlyPlayedSection } from "@/features/dashboard/components/recentlyPlayedSection.component";
import { ContinueListeningBar } from "@/features/dashboard/components/continueListeningBar.component";
import { UploadArea } from "@/features/dashboard/components/uploadArea.component";
import { StatisticsSection } from "@/features/dashboard/components/statisticsSection.component";
import { LowBalanceNudgeCard } from "@/features/onboarding/LowBalanceNudgeCard.component";
import { useTranslation } from "react-i18next";
import { useListenedTime } from "@/lib/podcast/hooks/useListenedTime.hook";
import {
	clearPendingUpload,
	takePendingUpload,
} from "@/lib/onboarding/pendingUpload";

export default function DashboardPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	// Use the workspace store with automatic smart polling
	const workspaces = usePodWorkspaceStore((state) => state.workspaces);
	const podcastsByWorkspace = usePodWorkspaceStore(
		(state) => state.podcastsByWorkspace,
	);

	// Fetch workspaces on mount and start smart polling if needed
	useEffect(() => {
		getWorkspaceActions().fetchWorkspacesWithPodcasts();

		// Cleanup: stop polling when component unmounts
		return () => {
			getWorkspaceActions().stopPolling();
		};
	}, []);

	// Note: Billing data refresh after checkout is handled by pricingSuccess page
	// before redirecting here, so no additional refresh needed.

	const refetch = () => {
		getWorkspaceActions().fetchWorkspacesWithPodcasts();
	};

	// Get total listened time
	const { stats: listenedTimeStats, isLoading: isLoadingListenedTime } =
		useListenedTime();

	// Subscribe to player state for showing which podcast is currently playing
	const currentlyPlayingPodcastId = usePlayerStore((state) => state.podcastId);
	const isPlayerPaused = usePlayerStore((state) => state.isPaused);

	// Track previous pause state to detect when playback stops
	const prevIsPausedRef = useRef(isPlayerPaused);

	useEffect(() => {
		// If we transitioned from playing (false) to paused (true)
		if (isPlayerPaused && !prevIsPausedRef.current) {
			refetch();
		}
		prevIsPausedRef.current = isPlayerPaused;
	}, [isPlayerPaused, refetch]);

	const [regeneratingPodcasts, setRegeneratingPodcasts] = useState<Set<string>>(
		new Set(),
	); // Track which podcasts are being regenerated
	const [activeChatWorkspaceId, setActiveChatWorkspaceId] = useState<
		string | null
	>(null); // Track which workspace's chat is open
	const [recentlyPlayedKey, setRecentlyPlayedKey] = useState(0);

	// Hook: Create Pod Workspace flow (modal + service integration)
	const { open: openCreateWorkspaceFlow, element: createWorkspaceElement } =
		useCreatePodWorkspaceFlow({
			onPodcastCreated: () => {
				// After the podcast has been created, immediately refetch workspaces
				// so the new podspace and its podcast show up without waiting for polling.
				refetch();
				// Also refresh the recently played section to show the new workspace if applicable
				setRecentlyPlayedKey((prev) => prev + 1);
			},
			onClose: () => {
				// When the modal closes, refetch to ensure any deleted podcasts are reflected
				refetch();
				setRecentlyPlayedKey((prev) => prev + 1);
			},
		});

	// create podworkspace button
	const createPodWorkspace = (file?: File, storageFileId?: string) => {
		openCreateWorkspaceFlow(file, storageFileId);
	};

	// Resume an upload started in the signup funnel, either from the query param
	// or — when that handoff was interrupted — from the stored pending upload.
	const resumedUploadRef = useRef(false);
	useEffect(() => {
		if (resumedUploadRef.current) return;

		const fromQuery = searchParams.get("storageFileId");
		const storageFileId = fromQuery ?? takePendingUpload();
		if (!storageFileId) return;

		resumedUploadRef.current = true;
		clearPendingUpload();
		if (fromQuery) {
			// Clear the query param to prevent re-triggering on refresh
			setSearchParams({});
		}
		createPodWorkspace(undefined, storageFileId);
	}, [searchParams, setSearchParams, createPodWorkspace]);

	// Handle play button click
	const handlePlayPodcast = async (podcastId: string) => {
		try {
			// Check if this podcast is already loaded in the player
			if (podcastId === currentlyPlayingPodcastId) {
				// If it's the same podcast, just toggle play/pause
				getPlayerActions().togglePlay();
			} else {
				// If it's a different podcast, load and play it
				await getPlayerActions().loadPodcast(podcastId, true);
			}
		} catch (error) {
			console.error("Failed to play podcast:", error);
		}
	};

	// Handle audio regeneration
	const handleRegenerateAudio = async (podcastId: string) => {
		// Add to regenerating set
		setRegeneratingPodcasts((prev) => new Set(prev).add(podcastId));

		try {
			const response = await PodcastService.triggerAudioGeneration(podcastId);
			console.log(
				`Audio generation triggered for podcast ${podcastId}`,
				response,
			);

			// Show feedback to user
			toast(
				`✅ Audio generation started for ${response.chaptersCount} chapters!\n\nThe audio is being generated in the background.\nThis may take several minutes.\n\nRefresh the page to see the updated status.`,
			);
		} catch (error) {
			console.error("Failed to trigger audio generation:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			toast(`❌ Failed to start audio generation:\n\n${errorMessage}`);
		} finally {
			// Remove from regenerating set after a short delay
			setTimeout(() => {
				setRegeneratingPodcasts((prev) => {
					const next = new Set(prev);
					next.delete(podcastId);
					return next;
				});
			}, 500);
		}
	};

	// Handle podcast retry
	const handleRetryPodcast = async (podcastId: string) => {
		try {
			await PodcastService.retryPodcast(podcastId);
			toast.success(t("workspace.list.retrySuccess"));
			// Refetch to update the UI
			refetch();
			// Also refresh the recently played section
			setRecentlyPlayedKey((prev) => prev + 1);
		} catch (error) {
			console.error("Failed to retry podcast:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			toast.error(`${t("workspace.errors.retryFailed")}: ${errorMessage}`);
		}
	};

	const handleOpenChat = (workspaceId: string) => {
		setActiveChatWorkspaceId(workspaceId);
	};

	// Callback after workspace deletion to refresh data
	const handleWorkspaceDeleted = () => {
		refetch();
		setRecentlyPlayedKey((prev) => prev + 1);
	};

	return (
		<div className="relative flex flex-1 flex-col overflow-hidden">
			{/* Modal element for the create workspace flow */}
			{createWorkspaceElement}
			<div className="flex-1 overflow-y-auto pb-16 md:pt-0">
				<div className="mx-auto md:max-w-[85%] px-4 pt-0 pb-4 md:p-8">
					{/* 1. Welcome Header - Hidden on Mobile */}
					<div className="hidden md:block mb-8">
						<h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-foreground to-foreground/60 bg-clip-text text-transparent">
							{t("dashboard.welcome")},{" "}
							{user?.displayName || user?.email?.split("@")[0]}
						</h1>
						<p className="text-muted-foreground text-lg">
							{t("dashboard.welcomeSubtitle")}
						</p>
					</div>

					{/* 2. Big Create Podcast Field - Desktop */}
					<div className="hidden md:block mb-12">
						<UploadArea onClick={createPodWorkspace} variant="desktop" />
					</div>

					{/* 2. Big Create Podcast Field - Mobile */}
					<div className="md:hidden mb-4">
						<UploadArea onClick={createPodWorkspace} variant="mobile" />
					</div>

					{/* Continue listening / create-next bar (Habit Loop v1) */}
					<ContinueListeningBar onCreateClick={() => createPodWorkspace()} />

					{/* Low / out-of-coins upgrade nudge for free users */}
					<LowBalanceNudgeCard />

					{/* Sections wrapper: on mobile stats come first, on desktop recent comes first */}
					<div className="flex flex-col">
						{/* 3. Recent Section - shows recently played and newly created podcasts */}
						<div className="order-2 md:order-1">
							<RecentlyPlayedSection
								key={recentlyPlayedKey}
								regeneratingPodcasts={regeneratingPodcasts}
								currentlyPlayingPodcastId={currentlyPlayingPodcastId}
								isPlayerPaused={isPlayerPaused}
								onPlayPodcast={handlePlayPodcast}
								onRegenerateAudio={handleRegenerateAudio}
								onOpenChat={handleOpenChat}
								onRetryPodcast={handleRetryPodcast}
								onWorkspaceDeleted={handleWorkspaceDeleted}
							/>
						</div>
						{/* 4. Statistics Section */}
						<StatisticsSection
							workspaces={workspaces}
							podcastsByWorkspace={podcastsByWorkspace}
							listenedTimeStats={listenedTimeStats}
							isLoadingListenedTime={isLoadingListenedTime}
						/>
					</div>
					{/* end flex wrapper */}
				</div>
			</div>

			{/* Global Chat Component */}
			{activeChatWorkspaceId && (
				<GlobalChat
					defaultOpen={true}
					onOpenChange={(open) => {
						if (!open) {
							setActiveChatWorkspaceId(null);
						}
					}}
					podWorkspaceId={activeChatWorkspaceId}
					workspaceTitle={
						workspaces.find((w) => w.id === activeChatWorkspaceId)?.title
					}
					forceNewChat={true}
				/>
			)}
		</div>
	);
}
